import supabase from "@/lib/supabase";
import { Quest } from "@/constant/schema";
import { Task } from "@/constant/tasks";

export interface Mistake {
  questionId: string;
  question: string;
  questId: string;
  questCover: string;
  accuracy: number;
  attempts: number;
  question_type: string;
  next_review_at: string;
  subject: string;
}

export interface SubjectMistakes {
  subject: string;
  mistakes: Mistake[];
  latestTime: string; // 该科目最新错题的时间
  questInfo: {
    questId: string;
    questCover: string;
  } | null; // 该科目最新错题对应的 quest 信息
}

const getContentFromQuestion = (question: any) => {
  if (!question) {
    return "";
  } else if (question.type === 'dictation') {
    return "Dictation: " + question.answer;
  } else if (question.type === 'grammar_drag_and_drop') {
    return "Drag and drop"
  } else {
    return question.question;
  }

}

const fetchAllAnswers = async (userId: string) => {
  // setIsLoading(true);
  // 获取用户的所有答案（包括正确和错误的）
  const { data: answers, error: answersError } = await supabase
    .from('user_answers')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (answersError) {
    console.error('获取答案失败:', answersError);
    throw answersError;
  }

  if (!answers || answers.length === 0) {
    // setAllAnswers([]);
    return [];
  }

  // 获取所有相关的question信息
  const questionIds = [...new Set(answers.map((answer: any) => answer.question_id))];
  const { data: questions, error: questionsError } = await supabase
    .from('questions')
    .select('id, type, question, answer, subject, mode_type, place_id')
    .in('id', questionIds);
  // throw new Error('test');
  if (questionsError) {
    console.error('获取问题信息失败:', questionsError);
    throw questionsError;
  }

  // 将答案和问题信息合并
  const enrichedAnswers = answers.map(answer => {
    const question = questions?.find(q => q.id === answer.question_id);
    return {
      ...answer,
      question_type: question?.type || '',
      question: getContentFromQuestion(question),
      question_subject: question?.subject || '',
      question_mode_type: question?.mode_type || '',
      question_place_id: question?.place_id || ''
    };
  });

  //   setAllAnswers(enrichedAnswers);
  return enrichedAnswers;
}

const fetchElementMap = async (elementIds: string[]) => {
  const { data, error } = await supabase
    .from('syllabus_element')
    .select('id, lesson_id')
    .in('id', elementIds);
  if (error) {
    console.error('Error fetching elements by IDs:', error);
    throw error;
  }
  return data.reduce((acc: any, element: any) => {
    acc[element.id] = element.lesson_id;
    return acc;
  }, {} as Record<string, string>);
}

const getQuestByPlaceId = (modeType: string, placeId: string, elementMap: Record<string, string>, allQuestsWithDetails: Quest[]) => {
  if (modeType === 'test') {
    return allQuestsWithDetails?.find((quest) => quest.id === quest.chapters.find((chapter: any) => chapter.id === placeId)?.quest_id);
  }
  let lessonId = '';
  if (modeType === 'learn') {
    lessonId = elementMap[placeId];
  } else {
    lessonId = placeId;
  }
  return allQuestsWithDetails?.find((quest) => quest.id === quest.chapters.find((chapter: any) => chapter.id === chapter.lesson_list.find((lesson: any) => lesson.id === lessonId)?.chapter_id)?.quest_id);
}

const fillQuestInfo = async (groupedAnswers: any[], allQuestsWithDetails: Quest[]) => {

  const elementIds: string[] = [];
  groupedAnswers.forEach((group: any) => {
    if (group.mode_type === 'learn') {
      elementIds.push(group.place_id);
    }
  });
  const elementMap = await fetchElementMap(elementIds);

  groupedAnswers.forEach((group: any) => {
    const quest = getQuestByPlaceId(group.mode_type, group.place_id, elementMap, allQuestsWithDetails);
    group.quest_id = quest?.id || '';
    group.quest_cover = quest?.cover_image || '';
  });
}

const processAnswers = async (answers: any[], allQuestsWithDetails: Quest[], subjectValue: string): Promise<Mistake[]> => {
  // 按 question_id 聚合所有答案
  const groupedAnswers = answers.reduce((acc, answer) => {
    if (answer.question_subject === subjectValue) {
      const questionId = answer.question_id;

      if (!acc[questionId]) {
        acc[questionId] = {
          questionId: questionId,
          question: answer.question,
          question_type: answer.question_type,
          mode_type: answer.question_mode_type,
          place_id: answer.question_place_id,
          totalAttempts: 0,
          correctAttempts: 0,
          hasMistakes: false,
        };
      }

      acc[questionId].totalAttempts++;
      if (answer.correct) {
        acc[questionId].correctAttempts++;
      } else {
        acc[questionId].hasMistakes = true; // 标记有错误答案
      }
    }
    return acc;
  }, {} as Record<string, any>);

  await fillQuestInfo(Object.values(groupedAnswers), allQuestsWithDetails);

  // 只返回有错误答案的问题，但正确率基于所有答案计算
  return Object.values(groupedAnswers)
    .filter((group: any) => group.hasMistakes) // 只显示有错误的问题
    .map((group: any) => ({
      questionId: group.questionId,
      question: group.question,
      questId: group.quest_id,
      questCover: group.quest_cover,
      question_type: group.question_type,
      accuracy: group.totalAttempts > 0 ? (group.correctAttempts * 100 / group.totalAttempts) : 0,
      attempts: group.totalAttempts,
      next_review_at: group.next_review_at,
      subject: group.subject,
    }));
}

const computeNextReviewAt = (answers: any[]) => {
  if (answers.length === 0) {
    console.log("No answers found");
    return null;
  }
  const sortedAnswers = (answers as any[]).sort((a: any, b: any) =>
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  let reviewStage = 0;
  let nextReviewAt = new Date(new Date(sortedAnswers[sortedAnswers.length - 1].created_at).getTime() + 1 * 24 * 60 * 60 * 1000);
  const answeredAt = new Date(sortedAnswers[sortedAnswers.length - 1].created_at);

  // 从最新的答案开始计算
  for (let i = sortedAnswers.length - 1; i >= 0; i--) {
    const answer = sortedAnswers[i];

    if (answer.correct) {
      // 答对，根据当前阶段计算
      if (reviewStage === 0) {
        // 第一次答对
        reviewStage = 1;
        nextReviewAt = new Date(answeredAt.getTime() + 3 * 24 * 60 * 60 * 1000); // +3 days
      } else if (reviewStage === 1) {
        // 第二次答对
        reviewStage = 2;
        nextReviewAt = new Date(answeredAt.getTime() + 7 * 24 * 60 * 60 * 1000); // +7 days
      } else if (reviewStage === 2) {
        // 第三次答对
        reviewStage = 3;
        nextReviewAt = new Date(answeredAt.getTime() + 14 * 24 * 60 * 60 * 1000); // +14 days
      } else if (reviewStage === 3) {
        // 第四次答对
        reviewStage = 4;
        nextReviewAt = new Date(answeredAt.getTime() + 30 * 24 * 60 * 60 * 1000); // +30 days
      } else if (reviewStage >= 4) {
        // 第四次之后还答对 → 可以归档
        nextReviewAt = new Date('2100-01-01T00:00:00.000Z'); // 设置为2100年
        break;
      }
    } else {
      break;
    }
  }
  return nextReviewAt;
}

const fetchAllSubjectsMistakes = async (userId: string, allQuestsWithDetails: Quest[]): Promise<Mistake[]> => {
  if (!userId || !allQuestsWithDetails) {
    return [];
  }
  const { data, error } = await supabase.rpc('get_mistakes', { p_subject: '', p_user_id: userId });
  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching all quests:', error);
    throw error;
  }
  await fillQuestInfo(data, allQuestsWithDetails);

  return data.map((item: any) => {

    return {
      questionId: item.question_id,
      question: item.question_text || item.questions?.[0]?.question,
      questId: item.quest_id,
      questCover: item.quest_cover,
      accuracy: item.correct_answers * 100 / item.total_answers,
      attempts: item.total_answers,
      question_type: item.question_type,
      next_review_at: item.next_review_at,
      subject: item.subject,
    }
  });
}

const sortMistakes = (answers: any[]) => {
  // 按 question_id 分组
  const groupedAnswers = answers.reduce((acc, answer) => {
    const questionId = answer.question_id;
    if (!acc[questionId]) {
      acc[questionId] = [];
    }
    acc[questionId].push(answer);
    return acc;
  }, {} as Record<string, any[]>);

  const results: any[] = [];

  Object.entries(groupedAnswers).forEach(([questionId, questionAnswers]) => {
    const answers = questionAnswers as any[];
    const nextReviewAt = computeNextReviewAt(answers);

    results.push({
      questionId,
      nextReviewAt: nextReviewAt?.toISOString() || null,
      totalAnswers: (questionAnswers as any[]).length,
      correctAnswers: answers.filter((a: any) => a.correct).length,
      timeUntilReview: nextReviewAt ? nextReviewAt.getTime() - new Date().getTime() : null
    });
  });

  // 按照时间差排序：已到复习时间的在前，然后是时间最近的
  return results.sort((a, b) => {
    // 没有 nextReviewAt 的排在最后
    if (!a.timeUntilReview && !b.timeUntilReview) return 0;
    if (!a.timeUntilReview) return 1;
    if (!b.timeUntilReview) return -1;

    // 按时间差排序：负数（已到时间）在前，正数（未到时间）在后
    return a.timeUntilReview - b.timeUntilReview;
  });
}

const fetchPendingMistakes = async (userId: string, allQuestsWithDetails?: Quest[]): Promise<SubjectMistakes[]> => {
  const answers = await fetchAllAnswers(userId);
  
  // 按 subject 分组，过滤掉没有 subject 的答案
  const groupedBySubject = answers.reduce((acc, answer) => {
    const subject = answer.question_subject;
    // 只处理有 subject 的答案
    if (subject && subject.trim() !== '') {
      if (!acc[subject]) {
        acc[subject] = [];
      }
      acc[subject].push(answer);
    }
    return acc;
  }, {} as Record<string, any[]>);

  // 为每个 subject 计算待复习错题
  const subjectMistakesList: SubjectMistakes[] = [];
  
  for (const [subject, subjectAnswers] of Object.entries(groupedBySubject)) {
    // 使用现有的 sortMistakes 函数计算待复习的错题
    const sortedMistakes = sortMistakes(subjectAnswers as any[]);
    
    // 过滤出需要复习的错题（未归档且已到复习时间）
    const pendingMistakes = sortedMistakes.filter(mistake => 
      mistake.timeUntilReview !== null && 
      mistake.timeUntilReview <= 0
    );

    // 只处理待复习错题数至少10道的科目
    if (pendingMistakes.length >= 10) {
      // 获取这些错题的详细信息（包含 quest 信息）
      const mistakeDetails = await processAnswers(subjectAnswers as any[], allQuestsWithDetails || [], subject);
      
      // 只添加需要复习的错题
      const subjectPendingMistakes = mistakeDetails.filter(mistake => {
        const sortedMistake = sortedMistakes.find(sm => sm.questionId === mistake.questionId);
        return sortedMistake && 
               sortedMistake.timeUntilReview !== null && 
               sortedMistake.timeUntilReview <= 0;
      });
      
      // 计算该科目的最新时间（基于答案的 created_at）
      const latestAnswer = (subjectAnswers as any[])
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      const latestTime = latestAnswer ? latestAnswer.created_at : new Date().toISOString();
      
      // 从错题详情中获取 quest 信息
      let questInfo = null;
      if (subjectPendingMistakes.length > 0) {
        const firstMistake = subjectPendingMistakes[0];
        if (firstMistake.questId) {
          questInfo = {
            questId: firstMistake.questId,
            questCover: firstMistake.questCover
          };
        }
      }
      
      subjectMistakesList.push({
        subject,
        mistakes: subjectPendingMistakes,
        latestTime,
        questInfo
      });
    }
  }

  return subjectMistakesList;
}

export { fetchAllAnswers, processAnswers, sortMistakes, fetchPendingMistakes, computeNextReviewAt, fetchAllSubjectsMistakes };